import { each, every, flatten, isEmpty, map } from 'lodash';
import { node } from 'prop-types';
import { createContext, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { createSearchParams, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { SanitizedHtml } from 'components';
import { useFetch, useSafeState, useUser } from 'hooks';
import { routes } from 'router';
import { leisureService } from 'services';
import { setCart } from 'store';

export const LeisureContext = createContext();

export const LeisureContextProvider = ({ children }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [confirmationModal, setConfirmationModal] = useSafeState({ open: false, content: null, alert: null });

  const { currentUser } = useUser();
  const { call: addTicketmasterCall, loading: ticketmasterLoading } = useFetch();
  const { call: leisureCall, data: leisure, loading: leisureLoading, error: errorLeisure } = useFetch();
  const {
    call: sessionsCall,
    data: sessions,
    loading: sessionsLoading,
    setData: setSessions,
    error: errorSessions
  } = useFetch({ withSetState: false });

  const leisureDateOptions = leisure?.sessions?.map((session) => ({
    ...session,
    value: session.id,
    // API returns a string with special characters so we need to sanitize it
    label: <SanitizedHtml content={session.label} />
  }));

  const emptySessions = useMemo(() => {
    return every(flatten(sessions?.map((s) => s.list)), (session) => isEmpty(session.categoryDisp));
  }, [sessions]);

  const { register, getValues, setValue, formState, control, watch, handleSubmit } = useForm({
    mode: 'onChange',
    defaultValues: {
      leisureDate: null,
      sessionGroup: []
    }
  });

  useEffect(() => {
    getLeisure();
  }, [id]);

  const getPlacementLabel = (placement) => {
    if (!placement) return '';

    if (placement.qtyMax === 0) {
      return `${placement.label} <span class="text-sm text-gray-500">(${t('products:soldOut')})</span>`;
    } else {
      return `${placement.label} <span class="text-sm text-gray-500">(${t('quantity')}: ${placement.qtyMax})</span>`;
    }
  };

  const placementOptions = (session) => {
    return map(session.categoryDisp, (placement) => ({
      ...placement,
      initialLabel: placement.label,
      label: <SanitizedHtml content={getPlacementLabel(placement)} />
    }));
  };

  const getLeisure = async () => {
    const searchParamsObj = Object.fromEntries(searchParams.entries());
    if (!id || !searchParamsObj?.typeItem) return;

    const { data } =
      (await leisureCall({
        service: 'castelis',
        method: 'get',
        path: `/product/leisures/${currentUser.serialNumber}/leisure/${id}/catalogue/${searchParamsObj.typeItem}/`
      })) || {};

    const firstSession = data?.body?.sessions?.[0];

    if (data?.body?.typeItem === 'TICKETMASTER' && !!firstSession?.id) {
      initSessions(firstSession);
    }
  };

  const initSessions = async (selectedSession) => {
    // Init select date with given session
    setValue('leisureDate', {
      ...selectedSession,
      value: selectedSession.id,
      id: selectedSession.id,
      label: <SanitizedHtml content={selectedSession.label} />
    });

    const { data: sessionsResponse } =
      (await sessionsCall(
        {
          service: 'castelis',
          method: 'get',
          path: `/product/leisures/${currentUser.serialNumber}/leisure/${id}/prices/${selectedSession.id}/`
        },
        { hideToast: true }
      )) || {};

    if (!isEmpty(sessionsResponse?.body)) {
      const sessionsList = [];

      // Init quantity and placement label
      each(sessionsResponse.body, (session) => {
        const findCategory = sessionsList.find((s) => s.categoryLabel === session.categoryLabel);
        const formattedSession = { ...session, selectedQty: 0 };
        if (!findCategory) {
          let selectedPlacement = null;

          if (!isEmpty(session.categoryDisp?.[0])) {
            selectedPlacement = {
              ...(session?.categoryDisp?.[0] || {}),
              label: <SanitizedHtml content={getPlacementLabel(session?.categoryDisp?.[0])} />
            };
          }

          sessionsList.push({
            categoryLabel: session.categoryLabel,
            selectedPlacement,
            list: [formattedSession]
          });
        } else {
          findCategory.list.push(formattedSession);
        }
      });

      setSessions(sessionsList);
      setValue('sessionGroup', sessionsList);
    }
  };

  const navigateToSearch = () => {
    navigate({
      pathname: routes.results.path,
      search: createSearchParams({ type: 'leisures' }).toString()
    });
  };

  const addTicketmasterToCart = async (onPlan, planValues = null) => {
    const { leisureDate, sessionGroup } = getValues();
    const { formatTicketmasterPayload, formatTicketmasterOnPlanModalContent, formatTicketmasterModalContent } =
      leisureService;

    let payload = {
      userId: currentUser?.serialNumber,
      articleId: onPlan && planValues ? planValues[0].idseanc : leisureDate.id,
      typeItem: 'TICKETMASTER',
      onPlan: onPlan ? 1 : 0,
      placement: []
    };

    payload = formatTicketmasterPayload({
      sessions: sessionGroup,
      planValues: onPlan ? planValues : null,
      payload,
      leisure,
      sessionId: leisureDate.id
    });

    if (isEmpty(payload) || ticketmasterLoading) return;

    const { data } =
      (await addTicketmasterCall({
        service: 'checkout',
        method: 'post',
        path: `/${currentUser?.serialNumber}/cart/ticketing/products`,
        data: { lines: payload }
      })) || {};

    if (data?.status === 200) {
      // Display success modal
      let content = onPlan
        ? formatTicketmasterOnPlanModalContent(sessionGroup, planValues)
        : formatTicketmasterModalContent(sessionGroup);
      content = content.map((item) => ({ ...item, label: leisure.title, vignette: leisure?.vignette }));

      setConfirmationModal({ open: true, content });
      dispatch(setCart(data?.body));
    } else {
      toast.error(t('cart:addError'));
    }
  };

  const leisureProviderValues = {
    leisure,
    leisureLoading,
    errorLeisure,
    initSessions,
    sessions,
    sessionsLoading,
    errorSessions,
    emptySessions,
    ticketmasterLoading,
    confirmationModal,
    setConfirmationModal,
    // Ticketmaster summary form,
    leisureDateOptions,
    placementOptions,
    // Methods
    navigateToSearch,
    addTicketmasterToCart
  };

  const formProviderValues = {
    register,
    getValues,
    setValue,
    control,
    watch,
    formState,
    submitTicketmasterForm: handleSubmit(() => addTicketmasterToCart(false))
  };

  return (
    <LeisureContext.Provider value={leisureProviderValues}>
      <FormProvider {...formProviderValues}>{children}</FormProvider>
    </LeisureContext.Provider>
  );
};

LeisureContextProvider.propTypes = {
  children: node.isRequired
};
